import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OverlayModule } from '@angular/cdk/overlay';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';

import {
  ButtonModule,
  IconModule,
  Nullable,
  SelectComponent,
  SimpleInputModule,
  SpinnerModule,
  TextAreaComponentModule,
  UploaderModule,
} from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, distinctUntilChanged, map, Observable, startWith } from 'rxjs';
import { environment } from '@fb/environments';

import {
  FinancialCategory,
  FloatingButtonEnvironmentOverrides,
  FloatingButtonUser,
  GetCoursesReq,
  GetFinancialCategoriesRes,
  GetLocationsRes,
  SupportCategories,
  SupportRequest,
} from '../../models';
import { WithNotRelatedOptionPipe } from '../../pipes/with-not-related-option.pipe';
import { TranslateInPipe } from '../../pipes/translate-in.pipe';
import { applyFloatingButtonEnvironmentOverrides } from '../../helpers/apply-floating-button-environment-overrides';
import { parseFloatingButtonEnvironmentOverrides } from '../../helpers/parse-floating-button-environment-overrides';
import { parseFloatingButtonUser } from '../../helpers/parse-floating-button-user';
import { FinanceQuestionEditorComponent } from './finance-question-editor.component';
import { DictionariesService } from '../../services/dictionaries.service';
import {
  FINANCE_CATEGORY_NAMES,
  III_WALLET_URLS,
  PAYSLIP_GUIDE_URLS,
} from './finance-category-description.constants';
import {
  EMPTY_FINANCE_PLACEHOLDER_KEY,
  getSupportFormGroup,
} from './support-form.constants';

@Component({
  selector: 'pu-support-form',
  imports: [
    OverlayModule,
    ButtonModule,
    IconModule,
    ReactiveFormsModule,
    SimpleInputModule,
    TextAreaComponentModule,
    UploaderModule,
    SelectComponent,
    WithNotRelatedOptionPipe,
    AsyncPipe,
    TranslateModule,
    SpinnerModule,
    TranslateInPipe,
    FinanceQuestionEditorComponent,
  ],
  templateUrl: './support-form.component.html',
  styleUrl: './support-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportFormComponent implements OnInit, OnChanges {
  readonly #formBuilder = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dictionariesService = inject(DictionariesService);
  readonly financeEditor = viewChild(FinanceQuestionEditorComponent);

  @Input() isLoading: boolean;
  @Input() requestIdHash: string;
  @Input() category: SupportCategories = null;
  @Input() courses: Nullable<Observable<GetCoursesReq>>;
  @Input() locations: Nullable<Observable<GetLocationsRes>>;
  @Input() financialCategories: Nullable<Observable<GetFinancialCategoriesRes>>;
  @Input() environmentOverrides?: FloatingButtonEnvironmentOverrides | string;
  @Input() user?: FloatingButtonUser | string;
  @Output() closeDialog = new EventEmitter<NonNullable<SupportRequest>>();

  assetsUrl = environment.assetsUrl;
  categoriesEnum = SupportCategories;
  financeCategoryNames = FINANCE_CATEGORY_NAMES;
  iiiWalletUrls = III_WALLET_URLS;
  userProfile = signal<FloatingButtonUser | null>(null);

  get apiHost(): string {
    return environment.apiHost;
  }

  apiS3Url = signal<Nullable<string>>(null);
  financeQuestionHtml = signal('');
  #boundFinancialCategories: Nullable<Observable<GetFinancialCategoriesRes>> = null;

  form: FormGroup;

  get acceptLabelKey(): string {
    return this.category === this.categoriesEnum.finance ? 'supportForm.files.allowed' : null;
  }

  get maxFileSize(): number {
    return this.category === this.categoriesEnum.finance ? 10 * 1024 * 1024 : null;
  }

  get maxFileSizeLabelKey(): string {
    return this.category === this.categoriesEnum.finance ? 'supportForm.files.size' : null;
  }

  get isFinanceMode(): boolean {
    return this.category === this.categoriesEnum.finance;
  }

  get isFinanceCategorySelected(): boolean {
    return !!this.form?.controls['financialCategory']?.value;
  }

  get isSubmitDisabled(): boolean {
    if (!this.isFinanceMode) {
      return this.form.invalid;
    }

    return !this.form.controls['financialCategory'].value;
  }

  ngOnInit(): void {
    this.form = getSupportFormGroup(this.#formBuilder, this.category);

    if (this.isFinanceMode) {
      this.#watchFinanceQuestionPlaceholder(this.financialCategories);
    }
  }

  ngOnChanges({ requestIdHash, environmentOverrides, financialCategories, user }: SimpleChanges): void {
    if (environmentOverrides) {
      applyFloatingButtonEnvironmentOverrides(
        parseFloatingButtonEnvironmentOverrides(environmentOverrides.currentValue),
      );
    }

    if (user) {
      this.userProfile.set(parseFloatingButtonUser(user.currentValue));
    }

    if (financialCategories?.currentValue && this.isFinanceMode && this.form) {
      this.#watchFinanceQuestionPlaceholder(financialCategories.currentValue);
    }

    if (requestIdHash?.currentValue && this.category === this.categoriesEnum.finance && this.form) {
      this.apiS3Url.set(`financial-requests/${this.requestIdHash}/files/`);
      this.form.controls['financialCategory'].setValidators(Validators.required);
      this.form.controls['financialCategory'].updateValueAndValidity();
    }
  }

  getSelectedCategory(categories: FinancialCategory[]): FinancialCategory | undefined {
    const categoryId = this.form.controls['financialCategory'].value;

    return categories.find(category => category.id === +categoryId);
  }

  payslipGuideUrl(): string {
    const user = this.userProfile();

    if (user?.businessUnit === 'Talk With Us') {
      return user?.staffType?.toLowerCase() === 'admin'
        ? PAYSLIP_GUIDE_URLS.twuAdmin
        : PAYSLIP_GUIDE_URLS.twuMass;
    }

    return PAYSLIP_GUIDE_URLS.redcore;
  }

  submit(): void {
    if (this.isFinanceMode && this.financeEditor()) {
      this.form.controls['text'].setValue(this.financeEditor()!.getHtml(), { emitEvent: false });
    }

    this.closeDialog.emit(this.form.getRawValue());
  }

  close(): void {
    this.closeDialog.emit();
  }

  isNotImage(fileName: string) {
    return /\.(pdf|docx|xlsx|doc|xls)$/i.test(fileName);
  }

  #watchFinanceQuestionPlaceholder(source: Nullable<Observable<GetFinancialCategoriesRes>>): void {
    if (!source || source === this.#boundFinancialCategories || !this.form) {
      return;
    }

    this.#boundFinancialCategories = source;
    const categoryControl = this.form.controls['financialCategory'];

    combineLatest([
      source.pipe(
        map(({ categories }) => categories),
        startWith([] as FinancialCategory[]),
      ),
      categoryControl.valueChanges.pipe(
        startWith(categoryControl.value),
        distinctUntilChanged(),
      ),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([categories, categoryId]) => {
        const id = categoryId == null || categoryId === '' ? null : +categoryId;
        const html = !id || Number.isNaN(id)
          ? this.#dictionariesService.translate(EMPTY_FINANCE_PLACEHOLDER_KEY, '')
          : categories.find(category => category.id === id)?.placeholder;

        if (html != null) {
          this.financeQuestionHtml.set(html);
        }
      });
  }
}
